import {
  ButtonERP,
  Dropzone,
  PrimeReactModal,
  RadioButtonERP,
} from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { postImportarExcel } from "api/Factura.service"
import {
  TIPO_DOCUMENTO_COMERCIAL,
  TipoDocumentoComercialType,
} from "api/Types/TipoDocumentoComercial"
import { STATUSCORRECT } from "common/components/ModalVerDocumento/constants"
import { STATIC_ASSETS_URL } from "common/context/constants"
import notification from "common/utils/notification"
import { downloadFile } from "features/Importar/constants"
import { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useHistory, useLocation } from "react-router-dom"
import { IMPORTAR_FACTURAS, PLANTILLA_FACTURAS } from "routes/RoutePaths"

function ImportarFacturasExcelModal() {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const [files, setFiles] = useState<File[]>([])
  const [modes, setModes] = useState<TipoDocumentoComercialType>(
    TIPO_DOCUMENTO_COMERCIAL.VENTA
  )

  const URL_PLANTILLA_FACTURAS = `${STATIC_ASSETS_URL}${PLANTILLA_FACTURAS}`

  const handleDownload = () => {
    downloadFile(URL_PLANTILLA_FACTURAS, "Plantilla_facturas.xlsx")
  }

  const replaceHash = () => {
    history.replace({
      hash: "",
    })
  }
  const handleFileAccepted = async (files: File[]) => {
    setFiles(files)
  }

  const handleDeleteFile = async () => {
    setFiles([])
  }

  const handleConfirm = async () => {
    if (!files) {
      throw new Error("Debes seleccionar un archivo para importar.")
    }

    try {
      const response = await postImportarExcel(modes, files[0])

      if (response.status === STATUSCORRECT) {
        notification.success({
          body: `${t("importar.facturaExc.msgSucess")}`,
        })
        replaceHash()
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Error: ${error.message}`)
      } else {
        throw new Error("Error desconocido en handleConfirm")
      }
    }
  }

  useEffect(() => {
    if (location.hash === IMPORTAR_FACTURAS) {
      setFiles([])
      setModes(TIPO_DOCUMENTO_COMERCIAL.VENTA)
    }
  }, [location.hash])

  return (
    <PrimeReactModal
      pt={{
        root: {
          className:
            "tw-p-4 tw-rounded-md tw-bg-white tw-shadow-lg tw-w-full tw-max-w-4xl tw-max-h-vhcalc-100 tw-m-4",
        },
        mask: {
          className: "tw-bg-black/50",
        },
        header: {
          className: "tw-flex tw-justify-between tw-items-center tw-pb-2",
        },
        headerTitle: {
          className: "tw-text-2xl tw-font-medium tw-text-primary",
        },
        headerIcons: {
          className: "tw-flex tw-justify-end",
        },
        content: {
          className: "tw-flex tw-flex-col tw-gap-4 tw-overflow-auto tw-h-full",
        },
        closeButton: {
          className: "tw-text-erp-primary tw-text-xl",
        },
      }}
      header={`${t("importar.facturaExc.title")}`}
      onHide={replaceHash}
      visible={Boolean(location.hash === IMPORTAR_FACTURAS)}
      closeOnEscape
      closable
      footer={
        <div
          key='Continuar'
          className='tw-pb-1 tw-flex tw-flex-col sm:tw-flex-row sm:tw-gap-4 tw-justify-end tw-pt-3'
        >
          <ButtonERP
            onClick={() => {
              replaceHash()
            }}
            label={`${t("importar.facturaExc.cancelButton")}`}
            customize={{
              type: "white",
              size: "md",
            }}
          />

          <ButtonERP
            onClick={() => {
              handleConfirm()
            }}
            label={`${t("importar.facturaExc.confirmButton")}`}
            customize={{
              type: "secondary",
              size: "md",
            }}
            disabled={!files?.length}
          />
        </div>
      }
    >
      <div className='tw-flex tw-flex-row tw-justify-between'>
        <div className='tw-flex tw-flex-row tw-items-center tw-gap-5 tw-mt-2 tw-mb-2'>
          <div className='tw-flex tw-flex-row tw-items-center tw-gap-2'>
            <RadioButtonERP
              checked={modes === TIPO_DOCUMENTO_COMERCIAL.VENTA}
              onChange={() => setModes(TIPO_DOCUMENTO_COMERCIAL.VENTA)}
            />
            <span className='tw-text-sm tw-text-nowrap'>
              {t("importar.facturaExc.typeVenta")}
            </span>
          </div>
          <div className='tw-flex tw-flex-row tw-items-center tw-gap-2'>
            <RadioButtonERP
              checked={modes === TIPO_DOCUMENTO_COMERCIAL.COMPRA}
              onChange={() => setModes(TIPO_DOCUMENTO_COMERCIAL.COMPRA)}
            />
            <span className='tw-text-sm tw-text-nowrap'>
              {t("importar.facturaExc.typeCompra")}
            </span>
          </div>
        </div>

        <div className='tw-flex tw-text-sm tw-items-center'>
          <Trans
            i18nKey='importar.facturaExc.downloadPlantilla'
            className='!tw-text-sm tw-text-nowrap'
            components={[
              <span
                className='tw-text-text-hyperlink tw-cursor-pointer tw-mr-0.5'
                key='0'
                onClick={() => handleDownload()}
              />,
            ]}
          />
        </div>
      </div>
      {!files?.length ? (
        <div className='tw-w-full tw-cursor'>
          <Dropzone
            maxFiles={1}
            accept={{
              "aplication/xml": [".xls", ".xlsx"],
            }}
            onFileAccepted={handleFileAccepted}
          />
        </div>
      ) : (
        <div className='tw-flex tw-grid-cols-[repeat(auto-fill,_5rem)] sm:tw-grid tw-flex-wrap tw-gap-4 sm:tw-justify-center tw-p-2 tw-overflow-auto tw-max-h-vhcalc-300'>
          {files.map((file) => (
            <div
              key={file.name}
              className='tw-relative tw-flex tw-flex-row tw-items-center sm:tw-flex-col tw-w-full sm:tw-w-20 sm:tw-text-center tw-shrink-0 tw-gap-2 tw-px-2'
            >
              <FontAwesomeIcon
                icon={["fal", "file-xml"]}
                className='tw-text-primary tw-w-9 tw-h-9'
              />
              <span className='tw-text-xs tw-text-center tw-line-clamp-2 tw-break-all tw-w-16'>
                {file.name}
              </span>
              <span
                className='tw-absolute tw-top-0 tw-right-1 tw-flex tw-justify-center tw-items-center tw-p-1 tw-rounded-md tw-bg-danger-10 hover:tw-shadow-sm tw-cursor-pointer'
                onClick={() => handleDeleteFile()}
              >
                <FontAwesomeIcon
                  icon={["fal", "xmark"]}
                  className='tw-text-danger tw-w-2 tw-h-2 tw-cursor-pointer'
                />
              </span>
            </div>
          ))}
        </div>
      )}
    </PrimeReactModal>
  )
}

export default ImportarFacturasExcelModal
